import {
    CreateLLMClassifierRequest,
    LLMCLassifierDetailsResponse,
    LLMCLassifierResponse,
    SlimExtractionLLMClassifierResponse,
    SlimLLMClassifierResponse,
    UnarchiveInfo,
    UpdateLLMClassifierRequest,
} from "models/LLMs";
import { BaseService } from "./BaseService";
import { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import { LLMVariable } from "models/LLMs/LLMVariable";
import { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import PublishedModelResultsReconcileResult from "models/PublishedModelResultsReconcileResult";
import {
    ApprovalEvent,
    type LLMClassifierApprovalEventSummary,
    type LLMClassifierApprovalReviewer,
    type RecordLLMClassifierApprovalEventRequest,
    type LLMClassifierApprovalMutationResponse,
} from "models/LLMs/LLMClassifierApproval";

export interface TargetedReclassifyRequest {
    limit: number;
    filters: ApplicationFilters;
}

export interface TargetedReclassifyResponse {
    numberOfConversationsReclassified: number;
}

class LLMClassifierService extends BaseService {
    protected urlBase = "api/LLMClassifier";

    public async getLLMClassifiers(abortSignal?: AbortSignal) {
        const apiPath = "";
        const llmClassifiers = await this.get<LLMCLassifierDetailsResponse[]>(
            new URLSearchParams(),
            apiPath,
            abortSignal,
        );

        return llmClassifiers;
    }

    public async getLLMClassifier(llmClassifierId: string) {
        return this.get<LLMCLassifierResponse>(
            new URLSearchParams(),
            llmClassifierId,
        );
    }

    public async getLLMClassifierVersion(
        llmClassifierId: string,
        implementationId: string,
    ) {
        return this.get<LLMCLassifierResponse>(
            new URLSearchParams(),
            `${llmClassifierId}/versions/${implementationId}`,
        );
    }

    public async getLLMClassifierVersions(llmClassifierId: string) {
        return this.get<LLMClassifierImplementation[]>(
            new URLSearchParams(),
            `${llmClassifierId}/versions`,
        );
    }

    public async getApprovalReviewers() {
        return this.get<LLMClassifierApprovalReviewer[]>(
            new URLSearchParams(),
            "approval-reviewers",
        );
    }

    public async recordApprovalEvent(
        llmClassifierId: string,
        implementationId: string,
        request: RecordLLMClassifierApprovalEventRequest,
    ): Promise<LLMClassifierApprovalMutationResponse> {
        const versionPath = `${llmClassifierId}/versions/${implementationId}`;

        switch (request.event) {
            case ApprovalEvent.ApprovalRequested:
                return this.post<LLMClassifierApprovalMutationResponse>(
                    JSON.stringify({
                        requestedApproverUserIds:
                            request.requestedApproverUserIds,
                        comparedToEntityId: request.comparedToEntityId,
                        comment: request.comment,
                    }),
                    new URLSearchParams(),
                    `${versionPath}/approval-requests`,
                );
            case ApprovalEvent.ApprovalRequestCanceled:
                return this.post<LLMClassifierApprovalMutationResponse>(
                    "{}",
                    new URLSearchParams(),
                    `${versionPath}/approval-requests/cancel`,
                );
            case ApprovalEvent.Approved:
            case ApprovalEvent.Rejected:
            case ApprovalEvent.ChangesRequested:
                return this.post<LLMClassifierApprovalMutationResponse>(
                    JSON.stringify({
                        event: request.event,
                        comment: request.comment,
                    }),
                    new URLSearchParams(),
                    `${versionPath}/approval-decisions`,
                );
            default:
                throw new Error("Unsupported approval event");
        }
    }

    public async getChangeRecord(
        llmClassifierId: string,
        implementationId: string,
    ) {
        return this.get<LLMClassifierApprovalEventSummary[]>(
            new URLSearchParams(),
            `${llmClassifierId}/versions/${implementationId}/change-record`,
        );
    }

    public async getSlimLLMClassifiers(abortSignal?: AbortSignal) {
        const apiPath = "slim";
        return this.get<SlimLLMClassifierResponse[]>(
            new URLSearchParams(),
            apiPath,
            abortSignal,
        );
    }

    public async getSlimExtractionLLMClassifiers(abortSignal?: AbortSignal) {
        const apiPath = "slim-extraction";
        return this.get<SlimExtractionLLMClassifierResponse[]>(
            new URLSearchParams(),
            apiPath,
            abortSignal,
        );
    }

    public async createLLMClassifier(
        llmClassifier: CreateLLMClassifierRequest,
    ): Promise<LLMCLassifierResponse> {
        return this.post(
            JSON.stringify(llmClassifier),
            new URLSearchParams(),
            "",
        );
    }

    public async updateLLMClassifier(
        llmClassifier: UpdateLLMClassifierRequest,
    ): Promise<LLMCLassifierResponse> {
        return this.put(
            new URLSearchParams(),
            llmClassifier.id,
            JSON.stringify(llmClassifier),
        );
    }

    public async deleteLLMClassifier(llmClassifierId: string) {
        return this.delete(new URLSearchParams(), llmClassifierId);
    }

    public async copyLLMClassifier(
        llmClassifierId: string,
    ): Promise<LLMCLassifierResponse> {
        return this.post("", new URLSearchParams(), `${llmClassifierId}/copy`);
    }

    public async archiveLLMClassifier(llmClassifierId: string): Promise<void> {
        const apiPath = `${llmClassifierId}/archive`;
        return this.post("", new URLSearchParams(), apiPath);
    }

    public async unarchiveLLMClassifier(
        llmClassifierId: string,
    ): Promise<UnarchiveInfo> {
        const apiPath = `${llmClassifierId}/un-archive`;
        return this.post("", new URLSearchParams(), apiPath);
    }

    public async publishLLMClassifier(
        llmClassifierId: string,
        implementationId: string,
    ): Promise<PublishedModelResultsReconcileResult> {
        const apiPath = `${llmClassifierId}/versions/${implementationId}/publish`;
        return this.post("", new URLSearchParams(), apiPath);
    }

    public async unpublishLLMClassifier(
        llmClassifierId: string,
    ): Promise<PublishedModelResultsReconcileResult> {
        const apiPath = `${llmClassifierId}/un-publish`;
        return this.post("", new URLSearchParams(), apiPath);
    }

    public async getVariables(): Promise<LLMVariable[]> {
        return this.get<LLMVariable[]>(new URLSearchParams(), "Variables");
    }

    public async targetedReclassify(
        classifierId: string,
        request: TargetedReclassifyRequest,
    ): Promise<TargetedReclassifyResponse> {
        return this.post(
            JSON.stringify(request),
            new URLSearchParams(),
            `${classifierId}/reclassify`,
        );
    }
}

export default LLMClassifierService;
